import React, { useState } from 'react';
import { Building, CreditCard, Check, ArrowRight, ArrowLeft, Shield, Users, Lock, Triangle, Plus, X, Info, ChevronDown, Loader2 } from 'lucide-react';
interface CheckoutClient {
  id: number;
  name: string;
  tier: 'starter' | 'growth' | 'enterprise';
}
interface CheckoutPageProps {
  onComplete: (orgName: string, clients: {
    name: string;
    tier: string;
  }[]) => void;
  onBack: () => void;
}
const TIER_INFO = {
  starter: {
    label: 'Starter',
    range: '0–250 identities',
    price: 499,
    color: 'text-gray-400',
    bg: 'bg-gray-400/10',
    border: 'border-gray-400/20'
  },
  growth: {
    label: 'Growth',
    range: '251–1,000 identities',
    price: 899,
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    border: 'border-blue-400/20'
  },
  enterprise: {
    label: 'Enterprise',
    range: '1,001–5,000 identities',
    price: 1499,
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    border: 'border-purple-400/20'
  }
};
function getVolumeDiscount(count: number) {
  if (count >= 50) return {
    pct: 0.2,
    label: '20% (50+ clients)'
  };
  if (count >= 26) return {
    pct: 0.15,
    label: '15% (26–50 clients)'
  };
  if (count >= 11) return {
    pct: 0.1,
    label: '10% (11–25 clients)'
  };
  return {
    pct: 0,
    label: ''
  };
}
export function CheckoutPage({
  onComplete,
  onBack
}: CheckoutPageProps) {
  const [step, setStep] = useState(1);
  const [orgName, setOrgName] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  // Client state
  const [clients, setClients] = useState<CheckoutClient[]>([]);
  const [newClientName, setNewClientName] = useState('');
  const [newClientTier, setNewClientTier] = useState<'starter' | 'growth' | 'enterprise'>('starter');
  // Payment state
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  // Pricing
  const subtotal = clients.reduce((sum, c) => sum + TIER_INFO[c.tier].price, 0);
  const discount = getVolumeDiscount(clients.length);
  const savings = Math.round(subtotal * discount.pct);
  const totalMonthly = subtotal - savings;
  const addClient = () => {
    const name = newClientName.trim();
    if (!name) return;
    setClients(prev => [...prev, {
      id: Date.now(),
      name,
      tier: newClientTier
    }]);
    setNewClientName('');
    setNewClientTier('starter');
  };
  const addSelfClient = () => {
    const name = orgName.trim() || 'My Organization';
    setClients(prev => [...prev, {
      id: Date.now(),
      name: `${name} (Internal)`,
      tier: 'starter'
    }]);
  };
  const removeClient = (id: number) => {
    setClients(prev => prev.filter(c => c.id !== id));
  };
  const updateClientTier = (id: number, tier: 'starter' | 'growth' | 'enterprise') => {
    setClients(prev => prev.map(c => c.id === id ? {
      ...c,
      tier
    } : c));
  };
  const handleNext = () => {
    if (step === 1 && !orgName.trim()) return;
    if (step === 2 && clients.length === 0) return;
    setStep(prev => Math.min(prev + 1, 3));
  };
  const handleBack = () => {
    if (step === 1) {
      onBack();
    } else {
      setStep(prev => Math.max(prev - 1, 1));
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 3) {
      setIsProcessing(true);
      setTimeout(() => {
        onComplete(orgName || 'My Organization', clients.map(c => ({
          name: c.name,
          tier: c.tier
        })));
      }, 2000);
    } else {
      handleNext();
    }
  };
  const canProceed = step === 1 ? orgName.trim().length > 0 : step === 2 ? clients.length > 0 : true;
  return <div className="min-h-screen bg-astro-dark text-white font-sans selection:bg-astro-green selection:text-black flex flex-col" data-id="element-857">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-astro-dark/80 backdrop-blur-md border-b border-astro-border h-16" data-id="element-858">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between" data-id="element-859">
          <div className="flex items-center gap-2" data-id="element-860">
            <Triangle className="text-astro-green fill-astro-green" size={24} strokeWidth={2.5} data-id="element-861" />

            <span className="text-xl font-bold text-white tracking-tight" data-id="element-862">
              Astrolayb
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-astro-text-secondary" data-id="element-863">
            <Lock size={14} data-id="element-864" />
            Secure Checkout
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center p-6 py-12" data-id="element-865">
        <div className="w-full max-w-2xl" data-id="element-866">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-10 relative px-8" data-id="element-867">
            <div className="absolute left-8 right-8 top-4 h-0.5 bg-astro-border" data-id="element-868"></div>
            <div className="absolute left-8 top-4 h-0.5 bg-astro-green transition-all duration-500 ease-out" style={{
            width: `${(step - 1) / 2 * 100}%`,
            maxWidth: 'calc(100% - 4rem)'
          }} data-id="element-869">
            </div>

            {[{
            num: 1,
            label: 'Organization',
            icon: <Building size={14} data-id="element-870" />
          }, {
            num: 2,
            label: 'Clients',
            icon: <Users size={14} data-id="element-871" />
          }, {
            num: 3,
            label: 'Payment',
            icon: <CreditCard size={14} data-id="element-872" />
          }].map(s => <div key={s.num} className="flex flex-col items-center gap-2 relative z-10" data-id="element-873">

                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${step > s.num ? 'bg-astro-green text-black' : step === s.num ? 'bg-astro-green text-black shadow-[0_0_12px_rgba(34,197,94,0.4)]' : 'bg-astro-card border border-astro-border text-astro-text-secondary'}`} data-id="element-874">

                  {step > s.num ? <Check size={14} data-id="element-875" /> : s.icon}
                </div>
                <span className={`text-xs font-medium transition-colors ${step >= s.num ? 'text-white' : 'text-astro-text-secondary'}`} data-id="element-876">

                  {s.label}
                </span>
              </div>)}
          </div>

          <div className="bg-astro-card border border-astro-border rounded-xl shadow-2xl overflow-hidden" data-id="element-877">
            <form onSubmit={handleSubmit} data-id="element-878">
              {/* ─── Step 1: Organization ─── */}
              {step === 1 && <div className="p-8" data-id="element-879">
                  <div className="mb-8" data-id="element-880">
                    <h2 className="text-2xl font-bold text-white mb-2" data-id="element-881">
                      Set up your organization
                    </h2>
                    <p className="text-astro-text-secondary" data-id="element-882">
                      This is your MSP or company that will manage client
                      security environments.
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-883">
                    <div data-id="element-884">
                      <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-885">
                        Organization Name *
                      </label>
                      <div className="relative" data-id="element-886">
                        <Building size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" data-id="element-887" />

                        <input type="text" required placeholder="e.g. Acme MSP" className="w-full bg-[#0a0e14] border border-astro-border rounded-lg px-4 py-2.5 pl-10 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" value={orgName} onChange={e => setOrgName(e.target.value)} autoFocus data-id="element-888" />

                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-889">
                      <div data-id="element-890">
                        <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-891">
                          Industry
                        </label>
                        <select className="w-full bg-[#0a0e14] border border-astro-border rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors appearance-none" value={industry} onChange={e => setIndustry(e.target.value)} data-id="element-892">

                          <option value="" data-id="element-893">Select…</option>
                          <option value="msp" data-id="element-894">Managed Services (MSP)</option>
                          <option value="financial" data-id="element-895">Financial Services</option>
                          <option value="healthcare" data-id="element-896">Healthcare</option>
                          <option value="technology" data-id="element-897">Technology</option>
                          <option value="government" data-id="element-898">
                            Government / Defense
                          </option>
                          <option value="manufacturing" data-id="element-899">Manufacturing</option>
                          <option value="education" data-id="element-900">Education</option>
                          <option value="other" data-id="element-901">Other</option>
                        </select>
                      </div>
                      <div data-id="element-902">
                        <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-903">
                          Company Size
                        </label>
                        <select className="w-full bg-[#0a0e14] border border-astro-border rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors appearance-none" value={companySize} onChange={e => setCompanySize(e.target.value)} data-id="element-904">

                          <option value="" data-id="element-905">Select…</option>
                          <option value="1-10" data-id="element-906">1–10 employees</option>
                          <option value="11-50" data-id="element-907">11–50 employees</option>
                          <option value="51-200" data-id="element-908">51–200 employees</option>
                          <option value="201-500" data-id="element-909">201–500 employees</option>
                          <option value="500+" data-id="element-910">500+ employees</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* ─── Step 2: Add Clients ─── */}
              {step === 2 && <div className="p-8" data-id="element-911">
                  <div className="mb-6" data-id="element-912">
                    <h2 className="text-2xl font-bold text-white mb-2" data-id="element-913">
                      Add your first clients
                    </h2>
                    <p className="text-astro-text-secondary" data-id="element-914">
                      Each client is an organization whose cloud security you'll
                      manage. You need at least one to start.
                    </p>
                  </div>

                  {/* Quick-add self option */}
                  {clients.length === 0 && <button type="button" onClick={addSelfClient} className="w-full mb-6 p-4 bg-astro-green/5 border border-astro-green/20 rounded-lg flex items-center gap-4 hover:bg-astro-green/10 transition-colors text-left group" data-id="element-915">

                      <div className="w-10 h-10 rounded-lg bg-astro-green/10 flex items-center justify-center text-astro-green shrink-0 group-hover:scale-105 transition-transform" data-id="element-916">
                        <Shield size={20} data-id="element-917" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-918">
                        <div className="text-sm font-bold text-white" data-id="element-919">
                          Scanning your own infrastructure?
                        </div>
                        <div className="text-xs text-astro-text-secondary mt-0.5" data-id="element-920">
                          Quick-add "{orgName || 'My Organization'}" as your
                          first client. You can rename it later.
                        </div>
                      </div>
                      <ArrowRight size={16} className="text-astro-green shrink-0" data-id="element-921" />

                    </button>}

                  {/* Add client form */}
                  <div className="flex gap-3 mb-4" data-id="element-922">
                    <input type="text" placeholder="Client name (e.g. Acme Corp)" className="flex-1 bg-[#0a0e14] border border-astro-border rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" value={newClientName} onChange={e => setNewClientName(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addClient();
                  }
                }} data-id="element-923" />

                    <select className="bg-[#0a0e14] border border-astro-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors appearance-none w-36" value={newClientTier} onChange={e => setNewClientTier(e.target.value as any)} data-id="element-924">

                      <option value="starter" data-id="element-925">Starter</option>
                      <option value="growth" data-id="element-926">Growth</option>
                      <option value="enterprise" data-id="element-927">Enterprise</option>
                    </select>
                    <button type="button" onClick={addClient} disabled={!newClientName.trim()} className="px-4 py-2.5 bg-astro-green text-black text-sm font-bold rounded-lg hover:bg-astro-green-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0" data-id="element-928">

                      <Plus size={16} data-id="element-929" /> Add
                    </button>
                  </div>

                  {/* Client list */}
                  {clients.length > 0 ? <div className="space-y-2 mb-6 max-h-[280px] overflow-y-auto pr-1" data-id="element-930">
                      {clients.map((client, i) => {
                  const tier = TIER_INFO[client.tier];
                  return <div key={client.id} className="flex items-center gap-3 p-3 bg-[#0a0e14] border border-astro-border rounded-lg group" data-id="element-931">

                            <div className="w-7 h-7 rounded bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-xs font-bold text-astro-text-secondary shrink-0" data-id="element-932">
                              {i + 1}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-933">
                              <div className="text-sm font-medium text-white truncate" data-id="element-934">
                                {client.name}
                              </div>
                            </div>
                            <select className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded border bg-transparent focus:outline-none cursor-pointer ${tier.color} ${tier.border}`} value={client.tier} onChange={e => updateClientTier(client.id, e.target.value as any)} data-id="element-935">

                              <option value="starter" className="bg-[#0a0e14] text-white" data-id="element-936">

                                Starter — $499
                              </option>
                              <option value="growth" className="bg-[#0a0e14] text-white" data-id="element-937">

                                Growth — $899
                              </option>
                              <option value="enterprise" className="bg-[#0a0e14] text-white" data-id="element-938">

                                Enterprise — $1,499
                              </option>
                            </select>
                            <span className="text-sm font-mono font-bold text-white w-16 text-right shrink-0" data-id="element-939">
                              ${tier.price}
                            </span>
                            <button type="button" onClick={() => removeClient(client.id)} className="p-1 text-astro-text-secondary hover:text-astro-danger transition-colors opacity-0 group-hover:opacity-100" data-id="element-940">

                              <X size={14} data-id="element-941" />
                            </button>
                          </div>;
                })}
                    </div> : <div className="py-8 text-center text-astro-text-secondary" data-id="element-942">
                      <Users size={32} className="mx-auto mb-3 opacity-30" data-id="element-943" />
                      <p className="text-sm" data-id="element-944">No clients added yet</p>
                      <p className="text-xs mt-1 opacity-70" data-id="element-945">
                        Add at least one client to continue
                      </p>
                    </div>}

                  {/* Running total */}
                  {clients.length > 0 && <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-4" data-id="element-946">
                      <div className="flex justify-between items-center mb-1" data-id="element-947">
                        <span className="text-sm text-astro-text-secondary" data-id="element-948">
                          {clients.length} client
                          {clients.length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-sm font-mono text-white" data-id="element-949">
                          ${subtotal.toLocaleString()}/mo
                        </span>
                      </div>
                      {discount.pct > 0 && <div className="flex justify-between items-center mb-1" data-id="element-950">
                          <span className="text-sm text-astro-green" data-id="element-951">
                            Volume discount ({discount.label})
                          </span>
                          <span className="text-sm font-mono text-astro-green" data-id="element-952">
                            -${savings.toLocaleString()}
                          </span>
                        </div>}
                      <div className="flex justify-between items-center pt-2 border-t border-astro-border mt-2" data-id="element-953">
                        <span className="text-sm font-bold text-white" data-id="element-954">
                          Estimated Monthly Total
                        </span>
                        <span className="text-xl font-bold text-white font-mono" data-id="element-955">
                          ${totalMonthly.toLocaleString()}
                        </span>
                      </div>
                    </div>}

                  {/* Info note */}
                  <div className="mt-4 p-3 bg-blue-500/5 border border-blue-500/15 rounded-lg flex items-start gap-3" data-id="element-956">
                    <Info size={14} className="text-blue-400 shrink-0 mt-0.5" data-id="element-957" />
                    <p className="text-xs text-blue-300/80 leading-relaxed" data-id="element-958">
                      Don't know exact identity counts? No problem — tiers
                      auto-adjust after your first scan. Start with your best
                      estimate. You can also add more clients anytime from the
                      dashboard.
                    </p>
                  </div>
                </div>}

              {/* ─── Step 3: Payment ─── */}
              {step === 3 && <div className="p-8" data-id="element-959">
                  <div className="mb-8" data-id="element-960">
                    <h2 className="text-2xl font-bold text-white mb-2" data-id="element-961">
                      Review & pay
                    </h2>
                    <p className="text-astro-text-secondary flex items-center gap-2" data-id="element-962">
                      <Lock size={14} data-id="element-963" /> Secure checkout powered by Stripe
                    </p>
                  </div>

                  {/* Order Summary */}
                  <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-5 mb-8" data-id="element-964">
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-astro-border" data-id="element-965">
                      <div data-id="element-966">
                        <h3 className="text-sm font-bold text-white" data-id="element-967">
                          {orgName}
                        </h3>
                        <p className="text-xs text-astro-text-secondary mt-0.5" data-id="element-968">
                          {clients.length} client
                          {clients.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <button type="button" onClick={() => setStep(2)} className="text-xs text-astro-green hover:underline font-medium" data-id="element-969">

                        Edit
                      </button>
                    </div>

                    <div className="space-y-2 mb-4 max-h-[160px] overflow-y-auto" data-id="element-970">
                      {clients.map(client => {
                    const tier = TIER_INFO[client.tier];
                    return <div key={client.id} className="flex items-center justify-between py-1.5" data-id="element-971">

                            <div className="flex items-center gap-2 min-w-0" data-id="element-972">
                              <span className="text-sm text-white truncate" data-id="element-973">
                                {client.name}
                              </span>
                              <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${tier.color} ${tier.bg} ${tier.border}`} data-id="element-974">

                                {tier.label}
                              </span>
                            </div>
                            <span className="text-sm font-mono text-white shrink-0 ml-3" data-id="element-975">
                              ${tier.price}/mo
                            </span>
                          </div>;
                  })}
                    </div>

                    <div className="border-t border-astro-border pt-3 space-y-1.5" data-id="element-976">
                      <div className="flex justify-between text-sm" data-id="element-977">
                        <span className="text-astro-text-secondary" data-id="element-978">
                          Subtotal
                        </span>
                        <span className="text-white font-mono" data-id="element-979">
                          ${subtotal.toLocaleString()}
                        </span>
                      </div>
                      {discount.pct > 0 && <div className="flex justify-between text-sm" data-id="element-980">
                          <span className="text-astro-green" data-id="element-981">
                            Volume discount ({discount.label})
                          </span>
                          <span className="text-astro-green font-mono" data-id="element-982">
                            -${savings.toLocaleString()}
                          </span>
                        </div>}
                      <div className="flex justify-between text-sm" data-id="element-983">
                        <span className="text-astro-text-secondary" data-id="element-984">
                          Setup fee
                        </span>
                        <span className="text-white font-mono" data-id="element-985">$0</span>
                      </div>
                      <div className="flex justify-between items-end pt-2 border-t border-astro-border mt-2" data-id="element-986">
                        <span className="text-sm font-bold text-white" data-id="element-987">
                          Due today
                        </span>
                        <span className="text-2xl font-bold text-astro-green font-mono" data-id="element-988">
                          ${totalMonthly.toLocaleString()}
                          <span className="text-sm font-normal text-astro-text-secondary" data-id="element-989">
                            /mo
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Form */}
                  <div className="space-y-4" data-id="element-990">
                    <div data-id="element-991">
                      <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-992">
                        Name on card
                      </label>
                      <input type="text" placeholder="Jane Doe" className="w-full bg-[#0a0e14] border border-astro-border rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" value={cardName} onChange={e => setCardName(e.target.value)} data-id="element-993" />

                    </div>
                    <div data-id="element-994">
                      <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-995">
                        Card number
                      </label>
                      <div className="relative" data-id="element-996">
                        <CreditCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" data-id="element-997" />

                        <input type="text" placeholder="4242 4242 4242 4242" className="w-full bg-[#0a0e14] border border-astro-border rounded-lg px-4 py-2.5 pl-10 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors font-mono" value={cardNumber} onChange={e => setCardNumber(e.target.value)} data-id="element-998" />

                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-999">
                      <div data-id="element-1000">
                        <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1001">
                          Expiry
                        </label>
                        <input type="text" placeholder="MM / YY" className="w-full bg-[#0a0e14] border border-astro-border rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors font-mono" value={expiry} onChange={e => setExpiry(e.target.value)} data-id="element-1002" />

                      </div>
                      <div data-id="element-1003">
                        <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1004">
                          CVC
                        </label>
                        <input type="text" placeholder="123" className="w-full bg-[#0a0e14] border border-astro-border rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors font-mono" value={cvc} onChange={e => setCvc(e.target.value)} data-id="element-1005" />

                      </div>
                    </div>
                  </div>

                  {/* Trust badges */}
                  <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-astro-border" data-id="element-1006">
                    <span className="text-[10px] text-astro-text-secondary flex items-center gap-1.5" data-id="element-1007">
                      <Lock size={10} data-id="element-1008" /> Secured by Stripe
                    </span>
                    <span className="text-[10px] text-astro-text-secondary" data-id="element-1009">
                      256-bit encryption
                    </span>
                    <span className="text-[10px] text-astro-text-secondary" data-id="element-1010">
                      SOC 2 compliant
                    </span>
                  </div>
                </div>}

              {/* ─── Footer Actions ─── */}
              <div className="p-6 border-t border-astro-border bg-[#0a0e14] flex items-center justify-between" data-id="element-1011">
                <button type="button" onClick={handleBack} disabled={isProcessing} className="px-4 py-2 text-sm font-medium text-astro-text-secondary hover:text-white transition-colors flex items-center gap-2 disabled:opacity-50" data-id="element-1012">

                  <ArrowLeft size={16} data-id="element-1013" />
                  {step === 1 ? 'Back' : 'Back'}
                </button>

                <button type="submit" disabled={!canProceed || isProcessing} className="px-6 py-2.5 bg-astro-green text-black text-sm font-bold rounded-lg hover:bg-astro-green-dark transition-colors shadow-[0_0_15px_rgba(34,197,94,0.2)] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px] justify-center" data-id="element-1014">

                  {isProcessing ? <>
                      <Loader2 size={16} className="animate-spin" data-id="element-1015" />
                      Processing…
                    </> : step === 3 ? <>Subscribe — ${totalMonthly.toLocaleString()}/mo</> : step === 2 ? <>
                      Continue to Payment <ArrowRight size={16} data-id="element-1016" />
                    </> : <>
                      Continue <ArrowRight size={16} data-id="element-1017" />
                    </>}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>;
}